import { useTranslation } from 'react-i18next';
import { useMemo, useState } from 'react';
import {
  WizardFlow,
  WizardFlowType,
  WizardState,
  WizardSuccessProps,
} from '@/features/wizard';
import { DebriefReviewStep, EditingSignalsMap } from './DebriefReviewStep';
import {
  debriefWizardFlow,
  WizardStep,
  DebriefFormData,
  WizardAnswers,
  Interaction,
  DebriefInteraction,
} from '@momentum/types';
import {
  useCreateDebrief,
  useEmployees,
  useGetDebrief,
  useUpdateDebrief,
  useCloseDebrief,
  useMyGoals,
} from '@/shared/hooks';
import {
  createFetchData,
  createPropsResolver,
  convertDomainStepToReactStep,
} from '@/routes/_dashboard/interactions/-components/WizardComponentUtils';
import { useEmployeeContext } from '@/shared/contexts/EmployeeContext';
import { toast } from 'sonner';
import { useNavigate } from '@tanstack/react-router';

interface DebriefWizardProps {
  // Optional existing debrief ID to load and continue
  existingDebriefId?: string;
  existingInteractionId?: string;
  // Optional custom completion handler (if not provided, will submit to API and navigate)
  onComplete?: (interaction?: Interaction) => void;
  // Optional initial state
  initialState?: Partial<WizardState>;
  // Wizard UI configuration
  showProgress?: boolean;
  showNavigation?: boolean;
  autoStart?: boolean;
  // Loading/error message translations (can be overridden)
  loadingMessage?: string;
  errorMessage?: string;
  firstStepId?: string;
  flowId?: string;
}

export const DebriefWizard = ({
  existingDebriefId,
  existingInteractionId,
  onComplete,
  initialState: externalInitialState,
  showProgress = false,
  showNavigation = true,
  autoStart = true,
  loadingMessage,
  errorMessage,
  firstStepId,
  flowId = debriefWizardFlow.id,
}: DebriefWizardProps) => {
  const { t } = useTranslation('wizard');
  const { t: tCommon } = useTranslation('common');
  const employee = useEmployeeContext();
  const {
    data: employees,
    isLoading: isLoadingEmployees,
    error: errorLoadingEmployees,
  } = useEmployees({});

  const {
    data: goals,
    isLoading: isLoadingGoals,
    error: errorLoadingGoals,
  } = useMyGoals(employee._id);

  const createDebriefMutation = useCreateDebrief();
  const updateDebriefMutation = useUpdateDebrief();
  const closeDebriefMutation = useCloseDebrief();
  const [showBot, setShowBot] = useState(false);
  const [wizardState, setWizardState] = useState<Partial<WizardState>>(
    externalInitialState ?? {}
  );
  const navigate = useNavigate();

  const createDebrief = async (
    props: WizardSuccessProps<DebriefFormData>,
    isDraft: boolean
  ) => {
    const creatorId = employee._id;
    const debriefData = {
      companyId: employee.companyId,
      creatorId,
      coreParticipants:
        props.answers['debrief-core-participants']?.map(
          (participant) => participant._id
        ) || [],
      otherParticipants:
        props.answers['debrief-other-participants']?.map(
          (participant) => participant._id
        ) || [],
      viewers:
        props.answers['debrief-viewers']?.map(
          (participant) => participant._id
        ) || [],
      formData: { [creatorId]: props.answers },
      goal: null, // TODO: Add goal
      isDraft,
    };

    let debrief: DebriefInteraction | undefined;
    if (existingInteractionId) {
      debrief = await updateDebriefMutation.mutateAsync({
        id: existingInteractionId,
        data: debriefData,
      });
    } else {
      debrief = await createDebriefMutation.mutateAsync(debriefData);
    }
    props?.onReset?.();
    toast.success(t('debriefQuestions.debriefSubmittedSuccessfully'));

    return debrief;
  };

  const mergeEditingSignals = (
    answers: Partial<WizardAnswers<DebriefFormData>>,
    editingSignals: EditingSignalsMap
  ): Partial<WizardAnswers<DebriefFormData>> => {
    const merged = { ...answers };
    const userSignals = editingSignals[employee._id];
    if (userSignals) {
      Object.entries(userSignals).forEach(([matchPointIndex, signalsList]) => {
        const signalKey =
          `debrief-match-point-${matchPointIndex}-signal` as keyof DebriefFormData;
        (merged as any)[signalKey] = signalsList;
      });
    }
    return merged;
  };

  const { data: existingDebrief, isLoading: isExistingDebriefLoading } =
    useGetDebrief(existingDebriefId);
  // Convert the domain wizard flow to React wizard flow
  const wizardFlow: WizardFlowType<DebriefFormData> = useMemo(() => {
    const fetchData = createFetchData(employees || [], goals || [], []);
    const resolver = createPropsResolver(fetchData);

    return {
      id: flowId,
      title: t('debriefQuestions.title'),
      description: t('debriefQuestions.description'),
      firstStepId: debriefWizardFlow.firstStepId,
      steps: debriefWizardFlow.steps.map((step) =>
        convertDomainStepToReactStep(
          step as WizardStep<DebriefFormData>,
          resolver,
          t,
          employee
        )
      ),
      // Add dynamic steps configuration
      dynamicSteps: debriefWizardFlow.dynamicSteps?.map((config) => ({
        afterStepId: config.afterStepId,
        answersStepId: config.answersStepId,
        generateSteps: (answers) => {
          // Generate domain steps
          const domainSteps = config.generateSteps(answers);
          // Convert to React steps
          return domainSteps.map((step) =>
            convertDomainStepToReactStep(
              step as WizardStep<DebriefFormData>,
              resolver,
              t,
              employee
            )
          );
        },
        nextStepId: config.nextStepId,
      })),
      successComponent: (props) => (
        <DebriefReviewStep
          {...props}
          existingDebrief={existingDebrief}
          employees={employees || []}
          onSendInteraction={async (editingSignals) => {
            try {
              if (existingDebrief) {
                // TODO: take the flow id
                const wizardState = sessionStorage.getItem(flowId);
                if (!wizardState) {
                  console.warn('unknown wizard state');
                  return;
                }

                const answers = mergeEditingSignals(
                  JSON.parse(wizardState)['state']['answers'],
                  editingSignals
                );
                const updatedDebrief = await updateDebriefMutation.mutateAsync({
                  id: existingDebrief._id,
                  data: {
                    formData: {
                      [employee?._id]: answers,
                    },
                  },
                });
                props?.onReset?.();
                toast.success(
                  t('debriefQuestions.debriefSubmittedSuccessfully')
                );
                if (onComplete) {
                  onComplete(updatedDebrief);
                } else {
                  sessionStorage.removeItem(flowId);
                  navigate({ to: '/' });
                }
              } else {
                const mergedAnswers = mergeEditingSignals(
                  props.answers,
                  editingSignals
                );
                const createdDebrief = await createDebrief(
                  { ...props, answers: mergedAnswers },
                  false
                );

                if (onComplete) {
                  onComplete(createdDebrief);
                } else {
                  sessionStorage.removeItem(flowId);
                  navigate({ to: '/' });
                }
              }
            } catch (error) {
              toast.error(t('debriefQuestions.debriefSubmittedFailed'), {
                description:
                  error instanceof Error ? error.message : 'Unknown error',
              });
            }
          }}
          onSaveDraft={async (editingSignals) => {
            try {
              const mergedAnswers = mergeEditingSignals(
                props.answers,
                editingSignals
              );
              await createDebrief({ ...props, answers: mergedAnswers }, true);
              sessionStorage.removeItem(flowId);
              navigate({ to: '/' });
            } catch (error) {
              toast.error(t('debriefQuestions.debriefSubmittedFailed'), {
                description:
                  error instanceof Error ? error.message : 'Unknown error',
              });
            }
          }}
          onSaveSignals={async (signals) => {
            try {
              if (!existingDebrief) return;

              // Get current user's form data
              const currentFormData =
                existingDebrief.formData[employee._id] || {};

              // Create updated form data with new signals
              const updatedFormData: Partial<WizardAnswers<DebriefFormData>> = {
                ...currentFormData,
              };

              // Update only the signals for the current user
              const userSignals = signals[employee._id];
              if (userSignals) {
                Object.entries(userSignals).forEach(
                  ([matchPointIndex, signalsList]) => {
                    const signalKey =
                      `debrief-match-point-${matchPointIndex}-signal` as keyof DebriefFormData;
                    updatedFormData[signalKey] = signalsList as any;
                  }
                );
              }

              // Send update with only formData
              await updateDebriefMutation.mutateAsync({
                id: existingDebrief._id,
                data: {
                  formData: {
                    [employee._id]: updatedFormData,
                  },
                },
              });

              toast.success(t('debriefQuestions.signalsUpdatedSuccessfully'));
            } catch (error) {
              toast.error(t('debriefQuestions.signalUpdateFailed'), {
                description:
                  error instanceof Error ? error.message : 'Unknown error',
              });
            }
          }}
          isSubmitting={false}
        />
      ),
      onComplete: () => {
        return null;
      },
    };
  }, [
    t,
    employees,
    createDebriefMutation,
    employee,
    existingDebrief,
    goals,
    flowId,
  ]);

  // Handle loading and error states
  if (isLoadingEmployees || isLoadingGoals) {
    return (
      <div className="container mx-auto p-6 max-w-4xl">
        <div className="flex items-center justify-center h-64">
          <div className="text-lg">
            {loadingMessage ?? tCommon('loadingData')}
          </div>
        </div>
      </div>
    );
  }

  if (errorLoadingEmployees || errorLoadingGoals) {
    return (
      <div className="container mx-auto p-6 max-w-4xl">
        <div className="flex items-center justify-center h-64">
          <div className="text-lg text-red-600">
            {errorMessage ?? tCommon('errorLoadingData')}
          </div>
        </div>
      </div>
    );
  }

  if (isExistingDebriefLoading) {
    return (
      <div className="container mx-auto p-6 max-w-4xl">
        <div className="flex items-center justify-center h-64">
          <div className="text-lg">{tCommon('loadingDebrief')}</div>
        </div>
      </div>
    );
  }

  function getAnswersExceptFromMatchPointAnalysis(
    answers: WizardAnswers<DebriefFormData>
  ) {
    return Object.entries(answers)
      .filter(([stepId]) => !stepId.startsWith('debrief-match-point-'))
      .reduce((acc, [key, value]) => {
        acc[key] = value;
        return acc;
      }, {} as Record<string, any>);
  }

  if (existingDebrief && !showBot) {
    return (
      <div className="container mx-auto p-6">
        <DebriefReviewStep
          onStartBot={() => {
            setWizardState({
              answers: getAnswersExceptFromMatchPointAnalysis(
                existingDebrief.formData?.[existingDebrief?.creatorId] || {}
              ),
            });
            setShowBot(true);
          }}
          answers={existingDebrief.formData?.[employee?._id] || {}}
          existingDebrief={existingDebrief}
          employees={employees || []}
          // onSaveDraft={() => {
          //   console.log('Saving draft - not implemented yet');
          // }}
          onSaveSignals={async (signals) => {
            try {
              // Get current user's form data
              const currentFormData =
                existingDebrief.formData[employee._id] || {};

              // Create updated form data with new signals
              const updatedFormData: Partial<WizardAnswers<DebriefFormData>> = {
                ...currentFormData,
              };

              // Update only the signals for the current user
              const userSignals = signals[employee._id];
              if (userSignals) {
                Object.entries(userSignals).forEach(
                  ([matchPointIndex, signalsList]) => {
                    const signalKey =
                      `debrief-match-point-${matchPointIndex}-signal` as keyof DebriefFormData;
                    updatedFormData[signalKey] = signalsList as any;
                  }
                );
              }

              // Send update with only formData
              await updateDebriefMutation.mutateAsync({
                id: existingDebrief._id,
                data: {
                  formData: {
                    [employee._id]: updatedFormData,
                  },
                },
              });

              toast.success(t('debriefQuestions.signalsUpdatedSuccessfully'));
            } catch (error) {
              toast.error(t('debriefQuestions.signalUpdateFailed'), {
                description:
                  error instanceof Error ? error.message : 'Unknown error',
              });
            }
          }}
          isSubmitting={false}
          closeDebrief={() => {
            closeDebriefMutation.mutate(existingDebrief._id);
          }}
          onUpdateViewers={async (viewerIds) => {
            try {
              await updateDebriefMutation.mutateAsync({
                id: existingDebrief._id,
                data: {
                  viewers: viewerIds,
                },
              });
              toast.success(
                t('debriefQuestions.viewers.updatedSuccessfully' as any)
              );
            } catch (error) {
              toast.error(t('debriefQuestions.viewers.updateFailed' as any), {
                description:
                  error instanceof Error ? error.message : 'Unknown error',
              });
            }
          }}
        />
      </div>
    );
  }

  return (
    <WizardFlow
      flow={wizardFlow}
      initialState={wizardState}
      showProgress={showProgress}
      showNavigation={showNavigation}
      autoStart={autoStart}
      firstStepId={firstStepId}
    />
  );
};
